import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Users,
  MessageSquare,
  Settings,
  AlertCircle
} from 'lucide-react';
import  Card  from '@/components/ui/Card';
import  Button  from '@/components/ui/Button';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import ApiService from '../../../services/api';

const BusinessDetails = () => {
  const { id } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchBusinessDetails();
  }, [id]);

  const fetchBusinessDetails = async () => {
    try {
      const response = await ApiService.getBusinessDetails(id);
      setBusiness(response.data);
    } catch (error) {
      console.error('Error fetching business details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await ApiService.updateBusinessStatus(id, newStatus);
      setBusiness(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      console.error('Error updating business status:', error);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!business) return <div>Business not found</div>;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Building2 },
    { id: 'appointments', label: 'Appointments', icon: Calendar },
    { id: 'customers', label: 'Customers', icon: Users },
    { id: 'communications', label: 'Communications', icon: MessageSquare },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  return (
    <div className="space-y-6">
      {/* Business Header */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl font-bold text-primary-700">
                {business.name.charAt(0)}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold">{business.name}</h1>
              <div className="flex items-center space-x-4 mt-2">
                <span className={`px-2 py-1 rounded-full text-sm ${
                  business.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : business.status === 'trial'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {business.status}
                </span>
                <span className="text-gray-500">ID: {business.id}</span>
                <span className="text-gray-500">Created: {new Date(business.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={() => handleStatusChange('suspended')}>
              Suspend
            </Button>
            <Button>Contact Business</Button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex space-x-2 border-b">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-2 border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent hover:border-gray-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content based on active tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Business Info */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Business Information</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <span>{business.email}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <span>{business.phone}</span>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <span>{business.address}</span>
              </div>
            </div>
          </Card>

          {/* Subscription Info */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Subscription</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Current Plan</p>
                <p className="font-medium">{business.subscription.plan}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Billing Cycle</p>
                <p className="font-medium">{business.subscription.billingCycle}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Next Billing Date</p>
                <p className="font-medium">
                  {new Date(business.subscription.nextBillingDate).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">MRR</p>
                <p className="font-medium">${business.subscription.mrr}</p>
              </div>
            </div>
          </Card>

          {/* Usage Stats */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Usage Statistics</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Total Appointments</p>
                <p className="font-medium">{business.stats.totalAppointments}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Customers</p>
                <p className="font-medium">{business.stats.activeCustomers}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Messages Sent</p>
                <p className="font-medium">{business.stats.messagesSent}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">API Usage</p>
                <p className="font-medium">{business.stats.apiCalls} calls</p>
              </div>
            </div>
          </Card>

          {/* Recent Activity */}
          <Card className="lg:col-span-3 p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
            <div className="space-y-4">
              {business.recentActivity.map((activity, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <activity.icon className="w-4 h-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm">{activity.description}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Other tab content */}
      {activeTab === 'appointments' && (
        <div>Appointments content</div>
      )}
      
      {activeTab === 'customers' && (
        <div>Customers content</div>
      )}
      
      {activeTab === 'communications' && (
        <div>Communications content</div>
      )}
      
      {activeTab === 'settings' && (
        <div>Settings content</div>
      )}
    </div>
  );
};

export default BusinessDetails;